import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Menu, X } from "lucide-react";

export const GlobalNav = ({ user, onLogout, currentPage = "dashboard" }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', path: '/' },
    { key: 'projects', label: 'Projects', path: '/projects' },
    { key: 'invoices', label: 'Invoices', path: '/invoices' },
    { key: 'clients', label: 'Clients', path: '/clients' },
    { key: 'subscriptions', label: 'Subscriptions', path: '/subscriptions-manage' },
    { key: 'features', label: 'Features', path: '/features' },
    { key: 'pricing', label: 'Pricing', path: '/subscriptions', match: ['subscriptions', 'pricing'] },
    { key: 'providers', label: 'Providers', path: '/providers' },
    { key: 'settings', label: 'Settings', path: '/settings' },
    { key: 'help', label: 'Help', path: '/help' },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo and Business Name */}
          <div className="flex items-center space-x-2 md:space-x-4 min-w-0 flex-1">
            <button 
              onClick={() => navigate('/')}
              className="text-xl md:text-2xl font-bold brand-logo font-heading flex-shrink-0"
            >
              Paify
            </button>
            <span className="text-gray-400 hidden sm:block">&bull;</span>
            <span className="text-sm md:text-lg font-semibold text-gray-700 font-heading truncate hidden sm:block max-w-[120px] md:max-w-none">
              {user?.business_name}
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              <button 
                key={item.key}
                onClick={() => handleNavClick(item.path)}
                className={`text-sm font-medium font-body whitespace-nowrap ${
                  (item.match ? item.match.includes(currentPage) : currentPage === item.key) 
                    ? "text-black" 
                    : "text-gray-700 hover:text-black"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
          
          {/* Right side - User actions */}
          <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onLogout}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 font-body text-xs md:text-sm hidden md:flex"
            >
              Sign out
            </Button>
            
            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {/* Mobile Business Name */}
            <div className="sm:hidden pb-3 mb-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700 font-heading">{user?.business_name}</p>
            </div>
            
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.path)}
                className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium font-body transition-colors ${
                  (item.match ? item.match.includes(currentPage) : currentPage === item.key)
                    ? "bg-gray-100 text-black"
                    : "text-gray-700 hover:bg-gray-50 hover:text-black"
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Mobile Sign Out */}
            <div className="pt-3 mt-2 border-t border-gray-100">
              <button
                onClick={onLogout}
                className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium font-body text-red-600 hover:bg-red-50 transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default GlobalNav;
